import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { stringToColor } from './utilis';
import Y from 'yjs';
import { useAuthStore } from '../../../store/storeAuth';

export type Cursor = {
  id: string;
  color: string;
  x: number;
  y: number;
  timestamp: number;
  userName: string;
};

export function useCursorStateSynced(ydoc: Y.Doc) {
  const [cursors, setCursors] = useState<Cursor[]>([]);
  const { screenToFlowPosition } = useReactFlow();
  const authUser = useAuthStore(state => state.user);

  const cursorsMap: any = ydoc.getMap<Cursor>('cursors');
  const cursorId = ydoc.clientID.toString();

  const cursorColor = stringToColor(
    cursors?.findIndex(({ id }) => id === cursorId) ?? 0,
  );

  const MAX_IDLE_TIME = 10000;
  const CURSOR_SYNC_INTERVAL_MS = 33;
  const lastSyncRef = useRef(0);
  const trailingTimerRef = useRef<number>();
  // Flush any cursors that have gone stale.
  const flush = useCallback(() => {
    const now = Date.now();

    for (const [id, cursor] of cursorsMap) {
      if (now - cursor.timestamp > MAX_IDLE_TIME) {
        cursorsMap.delete(id);
      }
    }
  }, [cursorsMap]);

  const writeCursor = useCallback(
    (x: number, y: number) => {
      cursorsMap.set(cursorId, {
        id: cursorId,
        color: cursorColor,
        x,
        y,
        timestamp: Date.now(),
        userName: authUser?.name,
      });
    },
    [cursorsMap, cursorId, cursorColor, authUser?.name],
  );

  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!authUser?.id) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const now = Date.now();
      const elapsed = now - lastSyncRef.current;

      if (elapsed >= CURSOR_SYNC_INTERVAL_MS) {
        lastSyncRef.current = now;
        writeCursor(position.x, position.y);
        return;
      }

      window.clearTimeout(trailingTimerRef.current);
      trailingTimerRef.current = window.setTimeout(() => {
        lastSyncRef.current = Date.now();
        writeCursor(position.x, position.y);
      }, CURSOR_SYNC_INTERVAL_MS - elapsed);
    },
    [screenToFlowPosition, authUser?.id, writeCursor],
  );

  useEffect(() => {
    const timer = window.setInterval(flush, MAX_IDLE_TIME);
    const observer = () => {
      setCursors([...cursorsMap.values()]);
    };

    flush();
    setCursors([...cursorsMap.values()]);
    cursorsMap.observe(observer);

    return () => {
      cursorsMap.unobserve(observer);
      window.clearInterval(timer);
      window.clearTimeout(trailingTimerRef.current);
    };
  }, [flush]);

  const cursorsWithoutSelf = useMemo(
    () => cursors.filter(({ id }) => id !== cursorId),
    [cursors],
  );

  return [cursorsWithoutSelf, onMouseMove] as const;
}

export default useCursorStateSynced;
